// Subscription / Pro state — single source of truth for "is this user Pro?"
//
// Right now (beta), there's no real billing wired up. Every signed-in
// account is treated as Pro by default so beta testers see the full app.
// A developer-only localStorage flag lets us preview the Free experience
// during testing without losing real Pro access.
//
// When real billing (Stripe / RevenueCat / Apple IAP) lands, the
// `isPro()` function below becomes the single point we read the real
// subscription state from. No screen needs to know how billing works —
// it just calls `useIsPro()` and gates accordingly.

import { useEffect, useState } from "react";

const PREVIEW_FREE_KEY = "tallyplate.dev.previewFree";

/**
 * Sync read. Use this in non-React code (e.g. lib functions called from
 * event handlers). React components should prefer `useIsPro()` so the UI
 * re-renders when the preview-free flag toggles.
 */
export function isProSync(): boolean {
  // Beta: everyone is Pro unless they've toggled the dev preview-free flag.
  if (typeof window === "undefined") return true;
  try {
    return localStorage.getItem(PREVIEW_FREE_KEY) !== "1";
  } catch {
    return true;
  }
}

/**
 * React hook. Returns the current Pro state and re-renders if the
 * preview-free flag changes (via the storage event — works across tabs).
 */
export function useIsPro(): boolean {
  const [pro, setPro] = useState(() => isProSync());
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === PREVIEW_FREE_KEY) setPro(isProSync());
    }
    window.addEventListener("storage", onStorage);
    // Also listen for in-tab updates via custom event
    function onLocal() {
      setPro(isProSync());
    }
    window.addEventListener("tallyplate:tier-change", onLocal);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("tallyplate:tier-change", onLocal);
    };
  }, []);
  return pro;
}

/** Dev-only: toggle the preview-Free flag. Used by the Settings debug card. */
export function setPreviewFree(previewFree: boolean): void {
  if (typeof window === "undefined") return;
  try {
    if (previewFree) {
      localStorage.setItem(PREVIEW_FREE_KEY, "1");
    } else {
      localStorage.removeItem(PREVIEW_FREE_KEY);
    }
    window.dispatchEvent(new CustomEvent("tallyplate:tier-change"));
  } catch {
    /* private mode — flag toggle silently fails */
  }
}
